import { reactive } from 'vue'
import { Store } from 'vuex'
import { Router } from 'vue-router'
import { State as StoreState } from '@/store/index'
import { ErrorMsgState, useErrorMsg } from '@/model/components/common/message/errorMessage'
import { checkFlowRedirect } from '@/model/components/users/userNoAuth'
import * as log from '@/model/util/log/Logger'
import { useUserloginAuh } from '@/model/api/user/apiUserLoginAuth'
import { useRouterUtil, RouterTypeData } from '@/model/router/router'
import { setAuth } from '@/model/components/users/userAuthinfo'
import { useScreen } from '@/model/screen/screenTransition'
import { useStoreAction } from '@/model/store/store'
import { focusBlur } from '@/model/util/dom/domUtil'
import { State as authState, LoginRedirectState } from './userLoginAuthState'

/**
 * ログイン認証画面のDOM初期処理を実施する
 * @param state 状態
 * @param store vuex
 * @param router vue router
 */
const initDom = async (state: authState, store: Store<StoreState>, router: Router) => {
  // 初期処理チェック(F5等でusernameが未設定の場合はTOP画面に遷移)
  await checkFlowRedirect(state.username, store, router)
}

/**
 * ログイン認証画面の初期状態を取得する
 * @param props propsデータ
 * @returns reactiveした初期状態
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useLoginAuth = (props: any): authState => {
  const state = reactive<authState>({
    loading: false,
    username: props.username,
    password: '',
    redirect: props.redirect,
    downloadKey: props.downloadKey,
    dispPassword: false,
  })
  return state
}

/**
 * ログイン認証を実施して画面遷移を実施する
 * @param state 画面状態
 * @param store vuex
 * @param router vueRouter
 * @param error エラーメッセージ状態
 */
const loginRedirect = async (state: authState, store: Store<StoreState>, router: Router, error: ErrorMsgState) => {
  // api送信を実施
  const { isNormal } = await useUserloginAuh(store).post(state, error)
  // ログイン認証に失敗した場合は終了
  if (!isNormal) {
    return
  }

  // ログイン認証情報設定
  const data: RouterTypeData.LoginTopData = {
    redirect: state.redirect,
    downloadKey: state.downloadKey,
  }
  const authResult = await setAuth(store, router, data)
  // 認証が成功した場合のみ後続の処理を実行
  if (!authResult) {
    return
  }
  // ログイン前処理
  await useStoreAction(store).loginBefore()
  // ログイン後画面に遷移
  useRouterUtil(router).loginAfter(data)
}

/**
 * ログイン認証を実施してデフォルトの画面遷移を実施する
 * @param state 画面状態
 * @param store vuex
 * @param router vueRouter
 * @param error エラーメッセージ状態
 */
const loginDefaultRedirect = async (
  state: LoginRedirectState,
  store: Store<StoreState>,
  router: Router,
  error: ErrorMsgState
) => {
  // username及びpasswordのみ設定
  const input: authState = {
    loading: false,
    username: state.username,
    password: state.password,
    redirect: state.redirect,
    downloadKey: state.downloadKey,
    dispPassword: false,
  }
  // ログイン認証処理を呼び出し
  log.debug(`loginDefaultRedirect: ${JSON.stringify(input)}`)
  await loginRedirect(input, store, router, error)
}

/**
 * ログイン認証を実施して画面遷移を実施する
 * @param state 画面状態
 * @param store vuex
 * @param router vueRouter
 * @param error エラーメッセージ状態
 */
const auth = async (state: authState, store: Store<StoreState>, router: Router, error: ErrorMsgState) => {
  // 一旦メッセージを全て初期化
  error.data.splice(0)
  // モバイルのためにfocusを外す
  focusBlur()
  // ローディングがONの場合は実行中とみなして何もしない
  if (state.loading) {
    log.debug(`Already running. [auth]`)
    return
  }
  // ローディングをONにする
  state.loading = true
  // api送信を実施
  await loginRedirect(state, store, router, error)
  // ローディングをOFFにする
  state.loading = false
}

/**
 * TOP画面に遷移する
 * @param state 画面状態
 * @param store vuex
 * @param router vueRouter
 */
const topRedirect = async (state: authState, store: Store<StoreState>, router: Router) => {
  // ローディングがONの場合は実行中とみなして何もしない
  if (state.loading) {
    log.debug(`Already running. [topRedirect]`)
    return
  }
  // TOP画面に遷移する
  const input: RouterTypeData.LoginTopData = {
    redirect: state.redirect,
    downloadKey: state.downloadKey,
  }
  useScreen().topRedirectNormal(store, router, input)
}

/**
 * ワンタイムパスワード発行画面遷移を実施する
 * @param state 画面状態
 * @param router vueRouter
 */
const resetpassword = async (state: authState, router: Router) => {
  // ワンタイムパスワード発行画面に遷移
  const data: RouterTypeData.EntryAuthSendData = {
    username: state.username,
    type: RouterTypeData.ENTRY_TYPE.RESET,
    redirect: state.redirect,
    downloadKey: state.downloadKey,
  }
  useRouterUtil(router).entryAuthSend(data)
}

export { initDom, useLoginAuth, loginRedirect, loginDefaultRedirect, auth, topRedirect, resetpassword, useErrorMsg }
